/**
 * platformFee.ts — Potidaea platform-fee routing (v1, posture-A / client-enforced).
 *
 * The Potidaea ASM platform charges a small fee on every swap ON TOP OF the
 * asset's own founder fee. The fee splits into two on-chain outputs:
 *   - platform slice   → PLATFORM_CASHADDR (Potidaea treasury)
 *   - DEA-yield slice  → DEA_YIELD_VAULT_ADDRESS (consensus-locked DistributionVault)
 *
 * v1 posture: client-enforced. The MBA covenants do NOT know about this fee;
 *   any TX built outside the Potidaea client bypasses it. This file adds the
 *   two outputs to every claim built by orbital.ts, so live user swaps fund
 *   DEA holders. Consensus-enforced enforcement is v2 (posture C, new covenant).
 *
 * Dust waiver: each slice must clear the 546-bit standard-relay dust floor.
 *   If either would be below dust, that slice is waived (0 emitted). Preserves
 *   low-value-swap-friendly semantics (mirrors DeviantOracleMBA's sub-dust fee
 *   waiver).
 *
 * Reference: navigate.st/bns/potidaea.asm/katalogos "DEA Yield" tab; and the
 * chipnet-verified mjs twin at scripts/lib/platform-fee.mjs.
 *   v1: platformFeeBps = 50 (0.5%); platformSplitBps = 4000 (40% platform / 60% DEA).
 *   v2: platformFeeBps = 30 (0.3%); platformSplitBps = 0     (100% DEA).
 */
import { cashAddressToLockingBytecode } from '@bitauth/libauth';
import type { TransactionBuilder } from 'cashscript';

export const DUST_BITS = 546n;

export interface PlatformFeeParams {
  readonly platformFeeBps: bigint;
  readonly platformSplitBps: bigint;
}

/** v1 parameter set — the Katalogos "DEA Yield · NOW" copy. */
export const PLATFORM_FEE_V1: PlatformFeeParams = {
  platformFeeBps: 50n,
  platformSplitBps: 4000n,
};

/** v2 parameter set — the "FUTURE" copy: 0.3% total, 100% to DEA. */
export const PLATFORM_FEE_V2: PlatformFeeParams = {
  platformFeeBps: 30n,
  platformSplitBps: 0n,
};

/**
 * Canonical chipnet addresses (v1). Derived from `.chipnet-keys-e.json` (oracle)
 * and `.chipnet-keys-c.json` (platform treasury) on 2026-09-18 by
 * `scripts/derive-dea-yield-vault.mjs`. Pinned here so the browser has no need
 * for filesystem access at boot; refresh in tandem with the on-disk canonical
 * `ChipnetTests/dea-yield-vault.json` file if either key rotates.
 */
export const PLATFORM_ADDRESSES = {
  chipnet: {
    deaYieldVaultAddress: 'bchtest:pdmx0gjaker3tgf97q05s24vqyseyazjg6uz0q5raq8g8kf92tkfgafuqp3d9',
    deaYieldVaultLBHex: 'aa207667a25db64715a125f01f482aac012192745246b8278283e80e83d92552ec9487',
    platformCashaddr: 'bchtest:qzgda5hsk96trkfk4qxa0t808kjwt8gzt5aqnt38dm',
    platformLBHex: '76a91490ded2f0b174b1d936a80dd7acef3da4e59d025d88ac',
  },
} as const;

export type PotidaeaNetwork = keyof typeof PLATFORM_ADDRESSES;

export interface PlatformFeeMath {
  readonly platformFeeTotal: bigint;
  readonly platformSats: bigint;
  readonly deaSats: bigint;
  readonly platformWaived: boolean;
  readonly deaWaived: boolean;
  readonly emittedTotal: bigint;
}

/**
 * Compute the platform-fee math from a gross-BCH figure. Mirrors what a v2
 * covenant would enforce. Returns dust-waiver flags for UI/telemetry.
 */
export function computePlatformFee(
  grossSats: bigint,
  params: PlatformFeeParams = PLATFORM_FEE_V1,
): PlatformFeeMath {
  if (grossSats < 0n) throw new Error(`grossSats must be non-negative, got ${grossSats}`);
  const platformFeeTotal = (grossSats * params.platformFeeBps) / 10000n;
  const rawPlatform = (platformFeeTotal * params.platformSplitBps) / 10000n;
  const rawDea = platformFeeTotal - rawPlatform;
  const platformWaived = rawPlatform > 0n && rawPlatform < DUST_BITS;
  const deaWaived = rawDea > 0n && rawDea < DUST_BITS;
  const platformSats = platformWaived ? 0n : rawPlatform;
  const deaSats = deaWaived ? 0n : rawDea;
  return {
    platformFeeTotal,
    platformSats,
    deaSats,
    platformWaived,
    deaWaived,
    emittedTotal: platformSats + deaSats,
  };
}

/**
 * appendPlatformFeeOutputs — mutates a cashscript TransactionBuilder in place,
 * appending the two fee outputs (in this order): platform, DEA-yield vault.
 * Returns the fee math so the caller can adjust its change output accordingly.
 */
export function appendPlatformFeeOutputs(
  tb: TransactionBuilder,
  grossSats: bigint,
  network: PotidaeaNetwork = 'chipnet',
  params: PlatformFeeParams = PLATFORM_FEE_V1,
): PlatformFeeMath {
  const fee = computePlatformFee(grossSats, params);
  const addrs = PLATFORM_ADDRESSES[network];
  if (fee.platformSats > 0n) tb.addOutput({ to: addrs.platformCashaddr, amount: fee.platformSats });
  if (fee.deaSats > 0n) tb.addOutput({ to: addrs.deaYieldVaultAddress, amount: fee.deaSats });
  return fee;
}

/** For WC's `sourceOutputs` shape we occasionally need raw LBs — helper. */
export function platformOutputLBs(network: PotidaeaNetwork = 'chipnet'): { platform: Uint8Array; deaVault: Uint8Array } {
  const addrs = PLATFORM_ADDRESSES[network];
  const p = cashAddressToLockingBytecode(addrs.platformCashaddr);
  const d = cashAddressToLockingBytecode(addrs.deaYieldVaultAddress);
  if (typeof p === 'string') throw new Error(`bad platform cashaddr: ${p}`);
  if (typeof d === 'string') throw new Error(`bad dea vault cashaddr: ${d}`);
  return { platform: p.bytecode, deaVault: d.bytecode };
}
